import { Modal, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid,
    Button
} from "zhihao-ui-react";

import { DEFAULT_VALUE } from "@/config";
import {
    HANDLING_ATTRIBUTE,
    PORT_BERTH_TYPE,
    PORT_WHARF_BERTH_GOODS_TYPE,
    PORT_WHARF_BERTH_TYPE
} from "@zhihaoscm/meta";
import { loadBerthManagementDetail } from "./BerthManagementFormPage";
import { deletePortManagement } from "./PortManagementDetailPage";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { BerthLocationModal } from "./BerthLocationModal";
import { useUserStore } from "@/stores/react/authStore";
import { RouterLink } from "@/views/components/RouterLink";

export interface BerthManagementDetailPageProps {
    berthId?: number | string;
    loadBerth?: (id: number) => Promise<PortWharfBerthVo>;
    deleteBerth?: (id: number, type: number) => Promise<unknown>;
    permissions?: string[];
}

const display = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : value;

/** 物流管理-泊位详情，覆盖 Vue 字段枚举、显示区域、编辑入口和 type=3 删除操作。 */
export function BerthManagementDetailPage({
    berthId,
    loadBerth = loadBerthManagementDetail,
    deleteBerth = deletePortManagement,
    permissions
}: BerthManagementDetailPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const currentId = berthId ?? searchParams.get("id") ?? undefined;
    const [detail, setDetail] = useState<PortWharfBerthVo>();
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 对齐 Vue v-auth：泊位详情的修改、删除入口分别受 DEAL/W 权限控制。
    const currentPermissions = permissions ?? storePermissions;
    const canDeal = currentPermissions.includes("ROLE_BERTH_DEAL");
    const canDelete = currentPermissions.includes("ROLE_BERTH_W");

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadBerth(Number(currentId))
            .then(value => {
                if (!disposed) setDetail(value);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("泊位详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, loadBerth]);

    const confirmDelete = async () => {
        if (!detail?.id) return;
        setDeleting(true);
        try {
            await deleteBerth(Number(detail.id), PORT_WHARF_BERTH_TYPE.BERTH.code);
            notifyOperationSuccess("已删除");
            setConfirmOpen(false);
            navigate(-1);
        } catch {
            setErrorMessage("泊位删除失败");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section aria-label="berth-management-detail-page" className="flex min-h-full flex-col">
            <div className="section-card">
                <ZhDetailHeader title="泊位详情" />
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {detail ? (
                    <ZhMultiGrid>
                        <ZhGrid cols={4} gap={[50, 20]}>
                            <ZhBaseItem label="名称" value={display(detail.name)} span={2} />
                            <ZhBaseItem label="简称" value={display(detail.shortName)} span={2} />
                            <ZhBaseItem label="所属码头" value={display(detail.portShortName)} />
                            <ZhBaseItem label="面积" value={detail.area ? `${detail.area}㎡` : DEFAULT_VALUE} />
                            <ZhBaseItem label="联系人" value={display(detail.contactName)} />
                            <ZhBaseItem label="手机号" value={display(detail.phone)} />
                            <ZhBaseItem label="位置" value={display(detail.fullAddress)} span={4} />
                            <ZhBaseItem label="经纬度" value={display(detail.latLon)} span={4} />
                            <ZhBaseItem
                                label="显示区域"
                                value={detail.displayArea?.length ? detail.displayArea.join(" ; ") : DEFAULT_VALUE}
                                span={4}
                            />
                            <ZhBaseItem
                                label="泊位类型"
                                value={PORT_BERTH_TYPE.getIntroByCode(detail.portBerthType) || DEFAULT_VALUE}
                            />
                            <ZhBaseItem
                                label="装卸属性"
                                value={HANDLING_ATTRIBUTE.getIntroByCode(detail.handlingAttribute) || DEFAULT_VALUE}
                            />
                            <ZhBaseItem
                                label="货物类型"
                                value={PORT_WHARF_BERTH_GOODS_TYPE.getIntroByCode(detail.goodsType) || DEFAULT_VALUE}
                            />
                            <ZhBaseItem label="简介" value={display(detail.remark)} span={4} />
                        </ZhGrid>
                    </ZhMultiGrid>
                ) : null}
                <Space className="mt-6">
                    {detail && canDeal ? (
                        banButton ? (
                            <Button disabled type="link">
                                修改
                            </Button>
                        ) : (
                            <RouterLink to={`/logistics/portWharfBerth/berthEdit?id=${detail.id}`}>修改</RouterLink>
                        )
                    ) : null}
                    {/* 对齐 Vue 的 v-if="berthInfo.latLon"：无坐标时不展示额外的位置入口。 */}
                    {detail?.latLon ? <BerthLocationModal berth={detail} /> : null}
                    {detail && canDelete ? (
                        <Button disabled={banButton} onClick={() => setConfirmOpen(true)}>
                            删除
                        </Button>
                    ) : null}
                    <Button type="link" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </div>
            <Modal
                title="提示"
                open={confirmOpen}
                okText="确认"
                cancelText="取消"
                confirmLoading={deleting}
                onCancel={() => setConfirmOpen(false)}
                onOk={() => void confirmDelete()}
            >
                是否删除泊位信息?
            </Modal>
        </section>
    );
}
